import React, { useRef, useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useTheme } from '../../contexts/ThemeContext';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

const { FiArrowLeft, FiDownload, FiPrinter, FiShare2, FiClock, FiEdit } = FiIcons;

const GeneratedPaper = ({ paper, onBack }) => {
  const paperRef = useRef(null);
  const { darkMode } = useTheme();
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  
  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPDF = async () => {
    if (!paperRef.current) return;
    
    setIsGeneratingPDF(true);
    try {
      // Store current background color
      const originalBg = document.body.style.backgroundColor;
      // Set background to white for PDF generation
      document.body.style.backgroundColor = 'white';
      
      // First page
      const canvas = await html2canvas(paperRef.current, {
        scale: 2, // Higher quality
        useCORS: true, // To handle external images
        logging: false,
        backgroundColor: '#ffffff',
      });
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
      });
      
      // Calculate dimensions
      const imgWidth = 210; // A4 width in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
      
      // Restore original background
      document.body.style.backgroundColor = originalBg;
      
      // Save the PDF
      pdf.save(`${paper.title || 'Exam_Paper'}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: paper.title,
        text: `Generated exam paper: ${paper.title}`,
        url: window.location.href,
      });
    } else {
      // Fallback - copy to clipboard
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };

  const formatDuration = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins} minutes`;
  };

  // Get font size class based on paper configuration
  const getFontSizeClass = () => {
    switch(paper.fontSize) {
      case 'small': return 'text-sm';
      case 'large': return 'text-lg';
      default: return 'text-base';
    }
  };

  // Get text alignment class based on configuration
  const getHeaderAlignmentClass = () => {
    switch(paper.headerAlignment) {
      case 'left': return 'text-left';
      case 'right': return 'text-right';
      default: return 'text-center';
    }
  };

  const getFooterAlignmentClass = () => {
    switch(paper.footerAlignment) {
      case 'left': return 'text-left';
      case 'right': return 'text-right';
      default: return 'text-center';
    }
  };

  return (
    <div className="space-y-6 p-8 ml-72">
      {/* Header */}
      <div className="flex justify-between items-center">
        <button
          onClick={onBack}
          className="flex items-center space-x-2 text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white transition-colors"
        >
          <SafeIcon icon={FiArrowLeft} />
          <span>Back to Generator</span>
        </button>

        <div className="flex items-center space-x-3">
          <button
            onClick={handleShare}
            className="flex items-center space-x-2 px-4 py-2 text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white border border-gray-300 dark:border-gray-600 rounded-lg transition-colors"
          >
            <SafeIcon icon={FiShare2} />
            <span>Share</span>
          </button>
          <button
            onClick={handlePrint}
            className="flex items-center space-x-2 px-4 py-2 text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white border border-gray-300 dark:border-gray-600 rounded-lg transition-colors"
          >
            <SafeIcon icon={FiPrinter} />
            <span>Print</span>
          </button>
          <button
            onClick={handleDownloadPDF}
            disabled={isGeneratingPDF}
            className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
          >
            <SafeIcon icon={isGeneratingPDF ? FiClock : FiDownload} className={isGeneratingPDF ? "animate-spin" : ""} />
            <span>{isGeneratingPDF ? "Generating..." : "Download PDF"}</span>
          </button>
        </div>
      </div>

      {/* Paper Content */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-100 dark:border-gray-700 print:shadow-none print:border-none">
        <div id="paper-content" ref={paperRef} className={`p-8 max-w-4xl mx-auto ${getFontSizeClass()} print:p-0 print:max-w-none print:mx-0`}>
          {/* Logo and Header */}
          <div className="mb-6 pb-4 border-b-2 border-gray-200 dark:border-gray-700 print:border-gray-800">
            <div className="flex flex-col items-center space-y-4">
              {paper.logo && (
                <div className="w-32 h-32 flex items-center justify-center">
                  <img src={paper.logo} alt="Institution Logo" className="max-w-full max-h-full object-contain" />
                </div>
              )}
              
              {paper.headerText && (
                <div className={`w-full ${getHeaderAlignmentClass()} text-gray-700 dark:text-gray-300 print:text-gray-800`}>
                  {paper.headerText}
                </div>
              )}
            </div>
            
            <div className="text-center mt-6">
              <h1 className="text-2xl font-bold text-gray-800 dark:text-white print:text-black mb-2">{paper.title}</h1>
              {paper.courseCode && (
                <p className="text-lg text-gray-600 dark:text-gray-300 print:text-gray-700 mb-4">Course: {paper.courseCode}</p>
              )}
              <div className="flex justify-center space-x-8 text-sm text-gray-600 dark:text-gray-400 print:text-gray-700">
                <div>
                  <strong>Duration:</strong> {formatDuration(paper.duration)}
                </div>
                <div>
                  <strong>Total Marks:</strong> {paper.totalMarks}
                </div>
                <div>
                  <strong>Questions:</strong> {paper.questions.length}
                </div>
              </div>
            </div>
          </div>

          {/* Instructions */}
          <div className="mb-8 p-4 bg-gray-50 dark:bg-gray-700 print:bg-gray-100 rounded-lg">
            <h3 className="font-semibold text-gray-800 dark:text-white print:text-black mb-2">Instructions:</h3>
            <ul className="text-sm text-gray-700 dark:text-gray-300 print:text-gray-800 space-y-1">
              <li>• Read all questions carefully before attempting</li>
              <li>• Answer all questions in the given time limit</li>
              <li>• Write clearly and legibly</li>
              <li>• Check your answers before submission</li>
            </ul>
          </div>

          {/* Questions by Section */}
          {paper.sections ? (
            paper.sections.map((section, sectionIndex) => {
              const sectionQuestions = paper.questions.filter(q => q.sectionName === section.name);
              
              return (
                <motion.div
                  key={section.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: sectionIndex * 0.1 }}
                  className="mb-8"
                >
                  <div className="flex justify-between items-center mb-4 pb-2 border-b border-gray-300 dark:border-gray-600 print:border-gray-400">
                    <h2 className="text-xl font-bold text-gray-800 dark:text-white print:text-black">{section.name}</h2>
                    <div className="text-sm text-gray-600 dark:text-gray-400 print:text-gray-700">
                      ({sectionQuestions.length} questions)
                    </div>
                  </div>

                  <div className="space-y-6">
                    {sectionQuestions.map((question, qIndex) => (
                      <div key={question.id} className="border-l-4 border-primary-200 dark:border-primary-800 print:border-primary-300 pl-4">
                        <div className="flex justify-between items-start mb-2">
                          <h4 className="font-medium text-gray-800 dark:text-white print:text-black">
                            Q{qIndex + 1}. {question.text}
                          </h4>
                          <span className="text-sm text-gray-600 dark:text-gray-400 print:text-gray-700 ml-4 flex-shrink-0">
                            [{question.assignedMarks || question.marks} marks]
                          </span>
                        </div>

                        {/* Multiple Choice Options */}
                        {question.type === 'Multiple Choice' && question.options && (
                          <div className="mt-3 ml-4">
                            {question.options.filter(opt => opt.trim()).map((option, optIndex) => (
                              <div key={optIndex} className="mb-1">
                                <span className="text-gray-700 dark:text-gray-300 print:text-gray-800">
                                  {String.fromCharCode(97 + optIndex)}. {option}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Answer Space for other question types */}
                        {question.type !== 'Multiple Choice' && (
                          <div className="mt-4">
                            <div className="border border-gray-200 dark:border-gray-600 print:border-gray-300 rounded p-4 min-h-[100px] bg-gray-50 dark:bg-gray-700/50 print:bg-white">
                              <p className="text-sm text-gray-500 dark:text-gray-400 print:text-gray-600 italic">Answer space</p>
                            </div>
                          </div>
                        )}

                        {/* Question metadata - hide in print */}
                        <div className="mt-2 text-xs text-gray-500 dark:text-gray-400 print:hidden">
                          Type: {question.type} | Difficulty: {question.difficulty}
                        </div>
                      </div>
                    ))}
                  </div>
                </motion.div>
              );
            })
          ) : (
            // Fallback for papers without sections
            <div className="space-y-6">
              {paper.questions.map((question, index) => (
                <div key={question.id} className="border-l-4 border-primary-200 dark:border-primary-800 print:border-primary-300 pl-4">
                  <div className="flex justify-between items-start mb-2">
                    <h4 className="font-medium text-gray-800 dark:text-white print:text-black">
                      Q{index + 1}. {question.text}
                    </h4>
                    <span className="text-sm text-gray-600 dark:text-gray-400 print:text-gray-700 ml-4 flex-shrink-0">
                      [{question.assignedMarks || question.marks} marks]
                    </span>
                  </div>

                  {/* Multiple Choice Options */}
                  {question.type === 'Multiple Choice' && question.options && (
                    <div className="mt-3 ml-4">
                      {question.options.filter(opt => opt.trim()).map((option, optIndex) => (
                        <div key={optIndex} className="mb-1">
                          <span className="text-gray-700 dark:text-gray-300 print:text-gray-800">
                            {String.fromCharCode(97 + optIndex)}. {option}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Answer Space for other question types */}
                  {question.type !== 'Multiple Choice' && (
                    <div className="mt-4">
                      <div className="border border-gray-200 dark:border-gray-600 print:border-gray-300 rounded p-4 min-h-[100px] bg-gray-50 dark:bg-gray-700/50 print:bg-white">
                        <p className="text-sm text-gray-500 dark:text-gray-400 print:text-gray-600 italic">Answer space</p>
                      </div>
                    </div>
                  )}

                  {/* Question metadata - hide in print */}
                  <div className="mt-2 text-xs text-gray-500 dark:text-gray-400 print:hidden">
                    Type: {question.type} | Difficulty: {question.difficulty}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Footer */}
          <div className="mt-12 pt-6 border-t-2 border-gray-200 dark:border-gray-700 print:border-gray-400">
            {paper.footerText && (
              <div className={`mb-4 ${getFooterAlignmentClass()} text-gray-600 dark:text-gray-400 print:text-gray-700`}>
                {paper.footerText}
              </div>
            )}
            
            <div className="text-center text-sm text-gray-600 dark:text-gray-400 print:text-gray-700">
              <p>Generated on {new Date(paper.generatedAt).toLocaleDateString()}</p>
              <p className="mt-1">*** End of Paper ***</p>
            </div>
            
            {/* Page numbers would be added via CSS for printing */}
            {paper.showPageNumbers && (
              <div className="text-right text-xs text-gray-400 dark:text-gray-500 print:hidden mt-4">
                Page numbers will appear when printed
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Print CSS */}
      <style jsx>{`
        @media print {
          @page {
            size: A4;
            margin: 1.5cm;
          }
          
          body {
            background: white;
            color: black;
          }
          
          /* Hide UI elements */
          header, nav, button, .print\\:hidden {
            display: none !important;
          }
          
          /* Page break between sections */
          .page-break {
            page-break-before: always;
          }
          
          /* Page numbers based on position */
          @page {
            ${paper.showPageNumbers && paper.pageNumberPosition?.includes('bottom') ? 
              '@bottom-left { content: counter(page); }' : ''}
            ${paper.showPageNumbers && paper.pageNumberPosition === 'bottom-center' ? 
              '@bottom-center { content: counter(page); }' : ''}
            ${paper.showPageNumbers && paper.pageNumberPosition === 'bottom-right' ? 
              '@bottom-right { content: counter(page); }' : ''}
            ${paper.showPageNumbers && paper.pageNumberPosition?.includes('top') ? 
              '@top-left { content: counter(page); }' : ''}
            ${paper.showPageNumbers && paper.pageNumberPosition === 'top-center' ? 
              '@top-center { content: counter(page); }' : ''}
            ${paper.showPageNumbers && paper.pageNumberPosition === 'top-right' ? 
              '@top-right { content: counter(page); }' : ''}
          }
        }
      `}</style>
    </div>
  );
};

export default GeneratedPaper;